import axios, { AxiosInstance, AxiosResponse } from 'axios';
import { getToken } from '../components/auth/authConfig';

const API_BASE_URL = `${process.env.REACT_APP_WEB_TOOL_API_BASE_URL}`; 

export interface ApiEmployee {
  id: number;
  name: string;
  email: string;
  role: string;
  associate_type: string;
  created_by: string;
  created_at: string;
}

export interface CreateEmployeeRequest {
  name: string;
  email: string;
  role: string;
  associate_type: string;
  created_by: string;
}

export interface UpdateEmployeeRequest {
  name: string;
  email: string;
  role: string;
  associate_type: string;
  created_by: string;
}

class ApiService {
  private axiosInstance: AxiosInstance;

  constructor() {
    this.axiosInstance = axios.create({
      baseURL: API_BASE_URL,
      timeout: 15000, // Increased timeout
      headers: {
        'Content-Type': 'application/json',
      },
    });

    // Request interceptor to add authentication token
    this.axiosInstance.interceptors.request.use(
      async (config) => {
        try {
          const token = await getToken();
          if (token) {
            config.headers['oidc-token'] = token;
          }
        } catch (error) {
          console.error('Error getting authentication token:', error);
          // Continue with request even if token retrieval fails
        }
        return config;
      },
      (error) => {
        return Promise.reject(error);
      }
    );

    // Response interceptor for error handling
    this.axiosInstance.interceptors.response.use(
      (response: AxiosResponse) => response,
      (error) => {
        let errorMessage = 'An unexpected error occurred';

        if (error.code === 'ECONNREFUSED' || error.code === 'ERR_NETWORK') {
          errorMessage = 'Unable to connect to server. Please ensure the backend server is running on http://127.0.0.1:5000';
        } else if (error.code === 'ECONNABORTED') {
          errorMessage = 'Request timeout. The server is taking too long to respond.';
        } else if (error.response) {
          // Server responded with error status
          const status = error.response.status;
          const data = error.response.data;
          
          switch (status) {
            case 401:
              errorMessage = 'Authentication failed. Please check your credentials.';
              break;
            case 403:
              errorMessage = 'Access denied. You do not have permission to perform this action.';
              break;
            case 404:
              errorMessage = 'Resource not found. Please check the API endpoint.';
              break;
            case 500:
              errorMessage = 'Internal server error. Please try again later.';
              break;
            default:
              errorMessage = data?.message || data?.error || `Server error (${status})`;
          }
        } else if (error.request) {
          // Request was made but no response received
          errorMessage = 'Network error: Unable to connect to server. Please check if the backend is running.';
        } else {
          // Something else happened
          errorMessage = `Request error: ${error.message}`;
        }

        console.error('API Error:', {
          message: errorMessage,
          originalError: error,
          config: error.config,
        });

        throw new Error(errorMessage);
      }
    );
  }

  async getUserList(): Promise<ApiEmployee[]> {
    try {
      const response = await this.axiosInstance.get<ApiEmployee[]>('/userslist');
      return response.data;
    } catch (error) {
      console.error('Error fetching user list:', error);
      throw error;
    }
  }

  async createUser(userData: CreateEmployeeRequest): Promise<ApiEmployee> {
    try {
      const response = await this.axiosInstance.post<ApiEmployee>('/onboarding/users', userData);
      return response.data;
    } catch (error) {
      console.error('Error creating user:', error);
      throw error;
    }
  }

  async updateUser(userId: number, userData: UpdateEmployeeRequest): Promise<ApiEmployee> {
    try {
      const response = await this.axiosInstance.post<ApiEmployee>(`/users/${userId}`, userData);
      return response.data;
    } catch (error) {
      console.error('Error updating user:', error);
      throw error;
    }
  }

  async deleteUser(userId: number): Promise<void> {
    try {
      await this.axiosInstance.delete(`/users/${userId}`);
    } catch (error) {
      console.error('Error deleting user:', error);
      throw error;
    }
  }

  // Health check method to test server connectivity
  async healthCheck(): Promise<boolean> {
    try {
      const response = await this.axiosInstance.get('/health', { timeout: 5000 });
      return response.status === 200;
    } catch (error) {
      return false;
    }
  }
}

export const apiService = new ApiService();